import i18next from "i18next";
import I18NextHttpBackend, {HttpBackendOptions} from "i18next-http-backend";
import I18nextBrowserLanguageDetector from "i18next-browser-languagedetector";
import I18NextLocalStorageBackend, {LocalStorageBackendOptions} from 'i18next-localstorage-backend';
import I18NextChainedBackend, {type ChainedBackendOptions} from "i18next-chained-backend";
import {initReactI18next} from "react-i18next";
import DateUtils from "./core/services/date-utils";
import CrispService from "./core/services/crisp";

export let I18NextTranslate: typeof i18next['t'];

/**
 * Initializes the internationalization (i18n) system for the application.
 */
export const initializeInternationalization = async () => {
    const instance = i18next
        .use(initReactI18next)
        .use(I18nextBrowserLanguageDetector)
        .use(I18NextChainedBackend)

    if (process.env.NODE_ENV === 'development') {
        const {HMRPlugin} = await import('i18next-hmr/plugin');
        instance.use(new HMRPlugin({
            webpack: {
                client: typeof window !== 'undefined',
                server: typeof window === 'undefined',
            },
            vite: {
                client: typeof window !== 'undefined',
            },
        }))
    }

    instance.on('languageChanged', (locale) => {
        DateUtils.onLocaleChange(instance.resolvedLanguage ?? locale);
        CrispService.setLocale(locale);
    })

    const globalTranslate = await instance.init<ChainedBackendOptions>({
        fallbackLng: 'en',
        fallbackNS: 'views',
        // debug: !EnvService.isProduction,
        debug: false,
        defaultNS: "views",
        ns: ['components', 'dialogs', 'form', 'validation', 'views', 'dynamic-values', '@bizkeytech'],
        load: 'languageOnly',
        supportedLngs: ['en', 'fr', 'es'],
        cacheInBuiltFormats: false,
        backend: {
            backends: [
                // First http so that if the user is offline, they can still see the last cached version of the translations
                // First local storage so that the user can see the translations even if they are offline
                I18NextHttpBackend,
                I18NextLocalStorageBackend,
            ],
            backendOptions: [
                {
                    loadPath: '/locales/{{lng}}/{{ns}}.json',
                } as HttpBackendOptions,
                {
                    prefix: 'i18next_translations_',
                    expirationTime: 7 * 24 * 60 * 60 * 1000, // cache for 7 days
                    version: '1.0.1',
                } as LocalStorageBackendOptions,
            ],
        },
    });

    I18NextTranslate = globalTranslate;
    return globalTranslate;
}